/* Org-admin "Dashboard Overview" — the all-branches view.
 *
 * This is deliberately NOT the branch dashboard with bigger numbers. An org
 * admin standing above every branch has a different job: not "clear today's
 * approvals" (that's the branch admin's screen, /home with a branch selected)
 * but "which branch needs me?". So every figure here is PER BRANCH and
 * comparable, plus the org-only facts that no branch dashboard can show —
 * branches running without an admin, employees mapped to no branch at all,
 * transfers waiting to be released, and payout per head.
 *
 * Rendered when isOrgOverview() is true (header filter on "All Branches").
 * Selecting a branch from the scorecard or header enters branch mode and loads
 * the operational dashboard for that branch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Activity,
    AlertTriangle,
    ArrowLeftRight,
    Building2,
    ChevronRight,
    Home,
    Inbox,
    RefreshCw,
    ShieldAlert,
    TrendingUp,
    UserMinus,
    UserPlus,
    Users,
    Wallet,
} from "lucide-react";
import { Line } from "react-chartjs-2";
import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from "chart.js";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import { ORG_DASHBOARD } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import QuickRegisterCandidateButton from "../../components/common/QuickRegisterCandidateButton";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { setActiveBranch } from "../../utils/branchSession";
import { hydrateDashboardSession } from "../../utils/customConfigSession";

// Registered here rather than relying on AdminDashboard's module side effect —
// this screen can mount without that module ever rendering. Re-registering the
// same components is a no-op.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

/* ------------------------------ presentation ------------------------------ */

const isDarkMode = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

/* The two variables plotted per branch. Both are a COUNT OF EMPLOYEES, so they
 * share one y-axis — a second scale (e.g. headcount against a percentage) would
 * let the lines cross wherever the axes were chosen to make them.
 *
 * One colour set for both themes: this pair clears the OKLCH lightness band,
 * the chroma floor and adjacent-pair colour-blind separation against BOTH the
 * light and dark card surfaces. The dash pattern and marker shape repeat that
 * identity, so the series stay distinguishable without colour. */
const BRANCH_SERIES = [
    { key: "present", label: "Present", color: "#059669", dash: [], pointStyle: "circle" },
    { key: "absent", label: "Absent", color: "#be123c", dash: [6, 4], pointStyle: "triangle" },
];

const ALERT_STYLES = {
    critical: "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800",
    warning: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
    info: "bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800",
};

const currency = (value) =>
    value === null || value === undefined
        ? "—"
        : `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const monthLabel = (period) =>
    period?.month
        ? dayjs(`${period.year}-${String(period.month).padStart(2, "0")}-01`).format("MMM YYYY")
        : "—";

const SectionCard = ({ icon: Icon, title, action, children, className = "", iconColor = "text-sidebar-primary" }) => (
    <div
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 p-5 ${className}`}
    >
        <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 flex items-center gap-2">
                {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
                {title}
            </h2>
            {action}
        </div>
        {children}
    </div>
);

const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
);

const KpiTile = ({ icon: Icon, label, value, sub, tone = "blue", loading }) => {
    const tones = {
        blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
        green: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
        amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
        violet: "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300",
        rose: "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300",
    };
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {label}
                </p>
                <span className={`p-2 rounded-lg ${tones[tone]}`}>
                    <Icon className="w-4 h-4" />
                </span>
            </div>
            {loading ? (
                <Skeleton className="h-8 w-20 mt-3" />
            ) : (
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-2">{value}</p>
            )}
            {sub && !loading && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>
            )}
        </div>
    );
};

/* Inline attendance meter — the table's own bar, so a 2-branch org doesn't get
 * a 2-bar chart. The number sits beside it (never colour alone). */
const AttendanceMeter = ({ pct, expected }) => {
    // Nobody was due in (whole branch on leave, or no employees): 0% would read
    // as a problem when there is nothing to measure.
    if (!expected) {
        return <span className="text-xs text-gray-400">n/a</span>;
    }
    const tone =
        pct >= 85 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500";
    return (
        <div className="flex items-center gap-2 min-w-[110px]">
            <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 w-11 text-right">
                {pct}%
            </span>
        </div>
    );
};

/* ------------------------------- attention -------------------------------- */

const AttentionStrip = ({ branches, unassigned, loading }) => {
    const items = useMemo(() => {
        const list = [];
        (branches || []).forEach((branch) => {
            (branch.alerts || []).forEach((alert) =>
                list.push({ ...alert, branch: branch.name, branchId: branch.id })
            );
        });
        if (unassigned?.headcount) {
            list.push({
                level: "critical",
                code: "unassigned",
                branch: "No branch",
                message: `${unassigned.headcount} employee(s) not mapped to any branch`,
            });
        }
        const rank = { critical: 0, warning: 1, info: 2 };
        return list.sort((a, b) => rank[a.level] - rank[b.level]);
    }, [branches, unassigned]);

    if (loading) return <Skeleton className="h-20 w-full rounded-2xl" />;

    return (
        <SectionCard
            icon={AlertTriangle}
            title="Needs your attention"
            iconColor="text-amber-500"
            action={
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {items.length} item{items.length === 1 ? "" : "s"}
                </span>
            }
        >
            {!items.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                    Every branch has an admin, employees mapped and healthy attendance today.
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {items.map((item, index) => (
                        <span
                            key={`${item.branchId}-${item.code}-${index}`}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-2 ${ALERT_STYLES[item.level]}`}
                        >
                            {item.level === "critical" ? (
                                <ShieldAlert className="w-3.5 h-3.5" />
                            ) : (
                                <AlertTriangle className="w-3.5 h-3.5" />
                            )}
                            <span className="font-semibold">{item.branch}</span>
                            <span className="opacity-70">·</span>
                            {item.message}
                        </span>
                    ))}
                </div>
            )}
        </SectionCard>
    );
};

/* ------------------------------- scorecard -------------------------------- */

const BranchScorecard = ({ branches, loading, onOpenBranch, payrollPeriod }) => {
    const columns = [
        "Branch", "Headcount", "Attendance", "Present", "Absent", "Leave / WFH",
        "Late", "Pending", "Corrections", "Incomplete", "Joined", "Exited",
        `Payout (${monthLabel(payrollPeriod)})`, "",
    ];

    return (
        <SectionCard
            icon={Building2}
            title="Branch scorecard"
            iconColor="text-teal-500"
            action={
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Select a branch to open it
                </span>
            }
        >
            {loading ? (
                <Skeleton className="h-56 w-full" />
            ) : !branches?.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    No active branches yet.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[1050px]">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                {columns.map((column) => (
                                    <th key={column} className="py-2 pr-4 font-semibold whitespace-nowrap">
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {branches.map((branch) => (
                                <tr
                                    key={branch.id}
                                    onClick={() => onOpenBranch(branch)}
                                    className="border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                                >
                                    <td className="py-3 pr-4">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                                                {branch.name}
                                            </span>
                                            {branch.is_main_branch && (
                                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">
                                                    MAIN
                                                </span>
                                            )}
                                            {!branch.admin_count && (
                                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">
                                                    NO ADMIN
                                                </span>
                                            )}
                                        </div>
                                        {branch.city && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{branch.city}</span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 font-semibold text-gray-800 dark:text-gray-100">
                                        {branch.headcount}
                                    </td>
                                    <td className="py-3 pr-4">
                                        <AttendanceMeter pct={branch.present_pct} expected={branch.expected} />
                                    </td>
                                    <td className="py-3 pr-4 text-emerald-600 dark:text-emerald-400 font-semibold">
                                        {branch.present}
                                    </td>
                                    <td className="py-3 pr-4 text-rose-600 dark:text-rose-400 font-semibold">
                                        {branch.absent}
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                        {branch.on_leave} / {branch.work_from_home}
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{branch.late}</td>
                                    <td className="py-3 pr-4">
                                        <span className={branch.pending.total ? "font-semibold text-amber-600 dark:text-amber-400" : "text-gray-400"}>
                                            {branch.pending.total}
                                        </span>
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                        {branch.corrections_pending}
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                        {branch.incomplete_logs}
                                    </td>
                                    <td className="py-3 pr-4 text-emerald-600 dark:text-emerald-400">
                                        +{branch.joined_this_month}
                                    </td>
                                    <td className="py-3 pr-4 text-rose-600 dark:text-rose-400">
                                        −{branch.exited_this_month}
                                    </td>
                                    <td className="py-3 pr-4 whitespace-nowrap text-gray-700 dark:text-gray-200">
                                        {branch.payout ? (
                                            <>
                                                <span className="font-semibold">{currency(branch.payout.total)}</span>
                                                <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                    {currency(branch.payout.per_head)} / head
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="py-3 text-gray-400">
                                        <ChevronRight className="w-4 h-4" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionCard>
    );
};

/* ---------------------------- present vs absent ---------------------------- */

/* Present and absent per branch, as two lines.
 *
 * Caveat worth knowing when reading it: branches are a CATEGORICAL axis, so the
 * slope between two of them carries no meaning — only the marker heights and the
 * gap between the two lines do. `tension: 0` keeps the segments straight rather
 * than smoothing them into an implied trend. If this ever needs to show a real
 * trend, the x-axis has to become time (which needs a per-day series from the
 * backend, not today's snapshot). */
const BranchPresenceLines = ({ branches, loading }) => {
    const dark = isDarkMode();
    const surface = dark ? "#1f2937" : "#ffffff";

    const data = useMemo(() => ({
        labels: (branches || []).map((branch) => branch.name),
        datasets: BRANCH_SERIES.map((series) => ({
            label: series.label,
            data: (branches || []).map((branch) => branch[series.key] || 0),
            borderColor: series.color,
            backgroundColor: series.color,
            borderWidth: 2,
            borderDash: series.dash,
            tension: 0,
            fill: false,
            pointStyle: series.pointStyle,
            pointRadius: 5,
            pointHoverRadius: 8,
            pointBackgroundColor: series.color,
            // Surface-coloured ring so overlapping markers stay separable.
            pointBorderColor: surface,
            pointBorderWidth: 2,
        })),
    }), [branches, surface]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        // Hovering anywhere on a branch reports both variables at once — the
        // comparison IS the point of the chart.
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    usePointStyle: true,
                    color: dark ? "#9ca3af" : "#4b5563",
                    font: { size: 11 },
                },
            },
            tooltip: {
                backgroundColor: "#111827",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 10,
                cornerRadius: 8,
                callbacks: {
                    label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: dark ? "#9ca3af" : "#4b5563", font: { size: 12, weight: "500" } },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Employees",
                    color: dark ? "#9ca3af" : "#4b5563",
                    font: { size: 11 },
                },
                ticks: { color: dark ? "#9ca3af" : "#4b5563", precision: 0 },
                grid: { color: dark ? "rgba(75,85,99,0.2)" : "rgba(229,231,235,0.8)" },
            },
        },
    };

    return (
        <SectionCard icon={Activity} title="Present vs absent by branch" iconColor="text-emerald-500">
            {loading ? (
                <Skeleton className="h-56 w-full" />
            ) : !branches?.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No branches to compare.</p>
            ) : (
                <div style={{ height: 300 }}>
                    <Line data={data} options={options} />
                </div>
            )}
        </SectionCard>
    );
};

/* ------------------------------- transfers -------------------------------- */

const TransfersCard = ({ transfers, loading }) => {
    const navigate = useNavigate();
    const rows = transfers?.recent || [];

    return (
        <SectionCard
            icon={ArrowLeftRight}
            title="Transfers in flight"
            iconColor="text-sky-500"
            action={
                <Button
                    variant="outline"
                    onClick={() => navigate("/employee/branch-transfer")}
                    className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                    Open
                </Button>
            }
        >
            {loading ? (
                <Skeleton className="h-40 w-full" />
            ) : !rows.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    No pending branch transfers.
                </p>
            ) : (
                <ul className="space-y-2">
                    {rows.map((row) => (
                        <li
                            key={row.id}
                            className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40"
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                    {row.employee || "—"}
                                    {row.employee_code && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                            {row.employee_code}
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    {row.from_branch || "—"}
                                    <ChevronRight className="w-3 h-3" />
                                    {row.to_branch || "—"}
                                </p>
                            </div>
                            <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 whitespace-nowrap">
                                {row.transfer_type}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </SectionCard>
    );
};

/* ------------------------------ movement card ----------------------------- */

const MovementCard = ({ totals, unassigned, loading }) => (
    <SectionCard icon={TrendingUp} title="Headcount movement this month" iconColor="text-violet-500">
        {loading ? (
            <Skeleton className="h-40 w-full" />
        ) : (
            <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                        <UserPlus className="w-3.5 h-3.5" /> Joined
                    </p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
                        {totals?.joined_this_month ?? 0}
                    </p>
                </div>
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
                    <p className="text-xs font-medium text-rose-700 dark:text-rose-300 flex items-center gap-1">
                        <UserMinus className="w-3.5 h-3.5" /> Exited
                    </p>
                    <p className="text-2xl font-bold text-rose-700 dark:text-rose-300 mt-1">
                        {totals?.exited_this_month ?? 0}
                    </p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Attrition</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                        {totals?.attrition_pct ?? 0}%
                    </p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Unmapped employees</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                        {unassigned?.headcount ?? 0}
                    </p>
                </div>
            </div>
        )}
    </SectionCard>
);

/* --------------------------------- the page -------------------------------- */

/* Mirror of AdminDashboard's hydration: for an org admin this screen is the
 * landing page, so it has to seed the same sessionStorage keys or downstream
 * flag-gated screens (customConfig / password policy) come up empty. */
const hydrateSession = (data) => {
    hydrateDashboardSession(data);
    const passwordPolicy = data?.password_policy?.[0];
    if (passwordPolicy) {
        sessionStorage.setItem("password_policy", JSON.stringify(passwordPolicy));
        window.dispatchEvent(new Event("hrms:session-storage-hydrated"));
    }
    if (data?.salary_calculation) {
        sessionStorage.setItem("salary_calculation", data.salary_calculation);
    }
};

const OrgOverviewDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));
    const pollingRef = useRef(null);
    const userName = sessionStorage.getItem("employee_name") || "Admin";

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setRefreshing(true);
        try {
            const response = await fetch(ORG_DASHBOARD, date ? { date } : {});
            if (response?.status === "success") {
                setData(response.data);
                hydrateSession(response.data);
            } else {
                ToastMsg("error", response?.message || "Failed to load the organisation dashboard");
            }
        } catch (err) {
            console.error("org dashboard error", err);
            ToastMsg("error", err?.message || "Something went wrong loading the organisation dashboard");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {
        pollingRef.current = setInterval(() => load(true), 60000);
        return () => clearInterval(pollingRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Opening a branch enters full branch mode so the complete sidebar menu appears.
    const openBranch = (branch) => {
        setActiveBranch(branch.id, branch.name, !!branch.is_main_branch);
        navigate("/home");
    };

    const totals = data?.totals;
    const branches = data?.branches || [];

    return (
        <ContainerCard scrollable className="h-full max-h-full">
        <div className="p-2 space-y-5 pb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-1">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                        Organisation Overview
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {userName} • {data?.branch_count ?? "—"} active branch
                        {data?.branch_count === 1 ? "" : "es"} • all branches
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <DatePicker
                        value={date ? dayjs(date) : null}
                        onChange={(d) => setDate(d ? d.format("YYYY-MM-DD") : "")}
                        format="DD-MM-YYYY"
                        allowClear={false}
                        size="large"
                        inputReadOnly
                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                        className="min-w-[160px]"
                    />
                    <Button
                        variant="outline"
                        onClick={() => load()}
                        disabled={refreshing}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                    <QuickRegisterCandidateButton />
                </div>
            </div>

            {/* Org rollup — the only aggregate numbers on this screen. */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KpiTile
                    icon={Home}
                    label="Branches"
                    value={data?.branch_count ?? "—"}
                    sub={
                        totals?.branches_without_admin
                            ? `${totals.branches_without_admin} without an admin`
                            : "all have an admin"
                    }
                    tone="blue"
                    loading={loading}
                />
                <KpiTile
                    icon={Users}
                    label="Total Workforce"
                    value={totals?.headcount ?? "—"}
                    sub={`${totals?.joined_this_month ?? 0} joined this month`}
                    tone="violet"
                    loading={loading}
                />
                <KpiTile
                    icon={Activity}
                    label="Attendance Today"
                    value={totals ? `${totals.present_pct}%` : "—"}
                    sub={`${totals?.present ?? 0} of ${totals?.expected ?? 0} expected`}
                    tone="green"
                    loading={loading}
                />
                <KpiTile
                    icon={Inbox}
                    label="Pending Approvals"
                    value={totals?.pending_total ?? "—"}
                    sub="across all branches"
                    tone="amber"
                    loading={loading}
                />
                <KpiTile
                    icon={Wallet}
                    label={`Payout ${monthLabel(data?.payroll_period)}`}
                    value={currency(totals?.payout)}
                    sub={data?.payroll_period ? `status: ${data.payroll_period.status}` : "no payroll run yet"}
                    tone="rose"
                    loading={loading}
                />
            </div>

            <AttentionStrip branches={branches} unassigned={data?.unassigned} loading={loading} />

            <BranchScorecard
                branches={branches}
                loading={loading}
                onOpenBranch={openBranch}
                payrollPeriod={data?.payroll_period}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BranchPresenceLines branches={branches} loading={loading} />
                <div className="space-y-4">
                    <MovementCard totals={totals} unassigned={data?.unassigned} loading={loading} />
                    <TransfersCard transfers={data?.transfers} loading={loading} />
                </div>
            </div>
        </div>
        </ContainerCard>
    );
};

export default OrgOverviewDashboard;
